import { db } from '@/lib/db';
import { isoDate } from '@/lib/format';
import { newId } from '@/lib/ids';
import { queue } from '@/lib/sync';
import type { CheckItem, CheckResult, Machine, VehicleCheck, VehicleCheckItem } from '@/types/models';

/**
 * The branch fleet (migration 0036). Every vehicle belongs to a site; the
 * quarry's own screens (diary, weighbridge, targets) list quarry vehicles
 * only, and the workshop and management see every site.
 */
export const QUARRY_SITE_ID = '00000000-0000-0000-0000-000000000001';

/** A vehicle of the quarry. Rows from before 0036 have no site: quarry. */
export const atQuarry = (m: Pick<Machine, 'site_id'>) => !m.site_id || m.site_id === QUARRY_SITE_ID;

/** The site a vehicle's diary belongs to (0053): its own site, the quarry
 *  for rows from before 0036. The server sets the same from the machine. */
export const diarySiteOf = (m: Pick<Machine, 'site_id'> | undefined) => m?.site_id || QUARRY_SITE_ID;

/** The Diary's site filter: one site's id, or every site. */
export const ALL_SITES = 'all';

export type DocState = 'expired' | 'due' | 'ok' | 'none';

/** Licence disc, COF and insurance: red when expired, amber inside 30 days. */
export function docState(expiry: string | null | undefined, today = isoDate()): DocState {
  if (!expiry) return 'none';
  if (expiry < today) return 'expired';
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + 30);
  return expiry <= isoDate(d) ? 'due' : 'ok';
}

export const DOCS: { key: 'licence_disc_expiry' | 'cof_expiry' | 'insurance_expiry'; label: string }[] = [
  { key: 'licence_disc_expiry', label: 'Licence disc' },
  { key: 'cof_expiry', label: 'COF / roadworthy' },
  { key: 'insurance_expiry', label: 'Insurance' },
];

/** The worst of a vehicle's document states. */
export function worstDoc(m: Partial<Machine>): { state: DocState; label: string | null; date: string | null } {
  let worst: { state: DocState; label: string | null; date: string | null } = { state: 'none', label: null, date: null };
  const rank: Record<DocState, number> = { none: 0, ok: 1, due: 2, expired: 3 };
  for (const d of DOCS) {
    const date = (m[d.key] as string | null | undefined) ?? null;
    const state = docState(date);
    if (rank[state] > rank[worst.state]) worst = { state, label: d.label, date };
  }
  return worst;
}

/* ------------------------------------------------------------------ */
/* The weekly sheet (0057): Saturday to Friday, no Sunday, as on the   */
/* client's paper checklist. Each day is still its own check.          */
/* ------------------------------------------------------------------ */

const addDays = (day: string, n: number) => {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoDate(d);
};

/** The Saturday that starts the sheet a day belongs to (a Sunday belongs
 *  to the sheet that started the day before). */
export function weekStart(day: string): string {
  const dow = new Date(`${day}T00:00:00`).getDay();   // 0 Sunday … 6 Saturday
  return addDays(day, -((dow + 1) % 7));
}

/** The sheet's days: Saturday, then Monday to Friday. */
export function weekDays(start: string): { day: string; label: string }[] {
  return [
    { day: start, label: 'Sat' },
    { day: addDays(start, 2), label: 'Mon' },
    { day: addDays(start, 3), label: 'Tue' },
    { day: addDays(start, 4), label: 'Wed' },
    { day: addDays(start, 5), label: 'Thu' },
    { day: addDays(start, 6), label: 'Fri' },
  ];
}

export const shiftWeek = (start: string, weeks: number) => addDays(start, weeks * 7);

/** A vehicle's checklist: its type's own list when it has one, otherwise
 *  the general list (rows with no type). Active rows, in sheet order. */
export function checklistFor(items: CheckItem[] | undefined, machineTypeId: string | null | undefined): CheckItem[] {
  const active = (items ?? []).filter((i) => i.is_active && !i.deleted_at);
  const own = machineTypeId ? active.filter((i) => i.machine_type_id === machineTypeId) : [];
  return (own.length ? own : active.filter((i) => !i.machine_type_id))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/** How many days a row needs on a full sheet. */
export const timesPerWeek = (i: Pick<CheckItem, 'frequency'>) =>
  i.frequency === 'weekly' ? 1 : i.frequency === 'twice_weekly' ? 2 : 6;

const OPEN_BREAKDOWN = ['reported', 'diagnosed', 'awaiting_parts', 'in_progress'];

/**
 * A defect on a critical row raises a breakdown (Jakes, 27 Oct 2026): the
 * vehicle's open breakdown if it already has one, so the same fault found
 * day after day is one breakdown, otherwise a new one for the workshop.
 * Returns the breakdown's id and whether it was new.
 */
export async function raiseCheckBreakdown(
  machine: Machine, checkId: string, description: string, reading: number | null, userId: string | null,
): Promise<{ id: string; created: boolean }> {
  const open = (await db.breakdowns.where('machine_id').equals(machine.id).toArray())
    .find((b) => !b.deleted_at && OPEN_BREAKDOWN.includes(String(b.status)));
  let id = open?.id as string | undefined;
  const created = !id;
  if (!id) {
    id = newId();
    await saveBranchBreakdown({
      id, machine_id: machine.id, category_id: null, severity: 'major',
      started_at: new Date().toISOString(), hour_meter_at_failure: reading,
      failure_description: `Found at the vehicle check: ${description}`,
      status: 'reported', action_taken: null, returned_to_service_at: null,
    }, userId);
  }
  await linkCheckToBreakdown(checkId, id);
  return { id, created };
}

/* ------------------------------------------------------------------ */
/* The daily vehicle check: the local mirror and the outbox, header    */
/* and items as one aggregate (upsert_vehicle_check on the server).    */
/* ------------------------------------------------------------------ */

export interface CheckDraft {
  id?: string;
  machine_id: string;
  site_id: string;
  check_date: string;
  meter_reading: number | null;
  meter_unit: 'hours' | 'km';
  driver_name: string | null;
  notes: string | null;
  breakdown_id: string | null;
  items: Record<string, { result: CheckResult; note: string | null; id?: string }>;
}

export async function saveVehicleCheck(
  d: CheckDraft, existing: VehicleCheck | undefined, userId: string | null,
): Promise<VehicleCheck> {
  const id = d.id ?? existing?.id ?? newId();
  const defects = Object.values(d.items).filter((i) => i.result === 'defect').length;
  const now = new Date().toISOString();
  const header = {
    ...(existing ?? {}),
    id,
    site_id: d.site_id,
    machine_id: d.machine_id,
    check_date: d.check_date,
    meter_reading: d.meter_reading,
    meter_unit: d.meter_unit,
    driver_name: d.driver_name?.trim() || null,
    notes: d.notes?.trim() || null,
    breakdown_id: d.breakdown_id,
    defects,
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? now,
    updated_at: now,
  } as VehicleCheck;

  const itemRows: VehicleCheckItem[] = Object.entries(d.items).map(([itemId, v]) => ({
    id: v.id ?? newId(),
    check_id: id,
    item_id: itemId,
    result: v.result,
    note: v.note?.trim() || null,
    updated_at: now,
  }) as VehicleCheckItem);

  await db.transaction('rw', [db.vehicle_checks, db.vehicle_check_items], async () => {
    await db.vehicle_checks.put(header);
    const old = await db.vehicle_check_items.where('check_id').equals(id).toArray();
    const keep = new Set(itemRows.map((r) => r.item_id));
    await db.vehicle_check_items.bulkDelete(old.filter((o) => !keep.has(o.item_id)).map((o) => o.id));
    await db.vehicle_check_items.bulkPut(itemRows.map((r) => {
      const prior = old.find((o) => o.item_id === r.item_id);
      return prior ? { ...r, id: prior.id } : r;
    }));
  });

  const stored = await db.vehicle_check_items.where('check_id').equals(id).toArray();
  await queue({
    entity: 'vehicle_check',
    entity_id: id,
    op: 'upsert',
    payload: {
      check: {
        id, machine_id: d.machine_id, check_date: d.check_date,
        meter_reading: d.meter_reading, driver_name: header.driver_name,
        notes: header.notes, breakdown_id: d.breakdown_id,
      },
      items: stored.map((r) => ({ id: r.id, item_id: r.item_id, result: r.result, note: r.note })),
    },
  });
  return header;
}

/* ------------------------------------------------------------------ */
/* A breakdown reported at a branch: through the outbox, so a driver   */
/* at a site without signal can still report it. The server numbers   */
/* it (BD-…) and puts it at the vehicle's site.                        */
/* ------------------------------------------------------------------ */

export interface BranchBreakdown {
  id: string;
  machine_id: string;
  category_id: string | null;
  severity: 'minor' | 'major' | 'critical';
  started_at: string;
  hour_meter_at_failure: number | null;
  failure_description: string;
  status: string;
  action_taken: string | null;
  returned_to_service_at: string | null;
  breakdown_no?: string | null;
  site_id?: string;
  created_by?: string | null;
  [k: string]: unknown;
}

export async function saveBranchBreakdown(b: BranchBreakdown, userId: string | null): Promise<void> {
  const existing = (await db.breakdowns.get(b.id)) as Record<string, unknown> | undefined;
  const row = {
    ...(existing ?? {}),
    ...b,
    breakdown_date: b.started_at.slice(0, 10),
    created_by: (existing?.created_by as string | undefined) ?? userId,
    updated_at: new Date().toISOString(),
  };
  await db.breakdowns.put(row);
  const payload: Record<string, unknown> = {
    id: b.id, machine_id: b.machine_id, category_id: b.category_id, severity: b.severity,
    started_at: b.started_at, hour_meter_at_failure: b.hour_meter_at_failure,
    failure_description: b.failure_description.trim(), status: b.status,
    action_taken: b.action_taken?.trim() || null,
    returned_to_service_at: b.returned_to_service_at,
    ended_at: ['repaired', 'closed'].includes(b.status) ? (b.returned_to_service_at ?? new Date().toISOString()) : null,
  };
  if (existing?.breakdown_no) payload.breakdown_no = existing.breakdown_no;
  await queue({ entity: 'breakdown', entity_id: b.id, op: 'upsert', payload });
}

/** Link a check to the breakdown it raised. */
export async function linkCheckToBreakdown(checkId: string, breakdownId: string): Promise<void> {
  const c = await db.vehicle_checks.get(checkId);
  if (!c) return;
  const items = await db.vehicle_check_items.where('check_id').equals(checkId).toArray();
  await db.vehicle_checks.put({ ...c, breakdown_id: breakdownId });
  await queue({
    entity: 'vehicle_check', entity_id: checkId, op: 'upsert',
    payload: {
      check: {
        id: c.id, machine_id: c.machine_id, check_date: c.check_date, meter_reading: c.meter_reading,
        driver_name: c.driver_name, notes: c.notes, breakdown_id: breakdownId,
      },
      items: items.map((r) => ({ id: r.id, item_id: r.item_id, result: r.result, note: r.note })),
    },
  });
}
